import { DialogProps } from '@lsy-netline/netline-ui/Dialog';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useProfileMenuItems, useUser } from 'hooks';
import { LogoutIcon } from 'icons';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
} from 'netline-ui';
import { NavigationArea } from 'types';
import { getDisplayName, getMainConfig } from 'utils';
import Divider from 'view/components/Divider/Divider';
import UserProfileMenuItem from 'view/components/UserProfileMenuItem/UserProfileMenuItem';

import styles, { mobileProfileMenuDialogClasses } from './MobileProfileMenuDialog.styles';

const MobileProfileMenuDialogRoot = styled(Dialog, {
  name: mobileProfileMenuDialogClasses.$name,
})(styles);

type MobileProfileMenuDialogProps = {
  className?: string;
  onLogoutClick: () => void;
} & DialogProps;

const MobileProfileMenuDialog = ({
  className,
  open,
  onClose,
  onLogoutClick,
  ...props
}: MobileProfileMenuDialogProps) => {
  const oidcConfig = getMainConfig('oidc');
  const { t } = useTranslation();
  const { user } = useUser();
  const profileMenuItems = useProfileMenuItems(NavigationArea.Mobile);

  const displayName = useMemo(() => getDisplayName(user?.profile), [user]);
  const handleMenuItemClick = useCallback(
    (menuItem, event) => {
      window.postMessage(
        { source: 'mobileprofilemenu', type: 'menuitemclick', payload: menuItem },
        window.location.origin,
      );
      onClose?.(event, 'escapeKeyDown');
    },
    [onClose],
  );

  const handleCloseClick = useCallback(
    (event) => {
      onClose?.(event, 'escapeKeyDown');
    },
    [onClose],
  );

  return (
    <MobileProfileMenuDialogRoot
      className={clsx(className, mobileProfileMenuDialogClasses.root)}
      open={open}
      {...props}
    >
      <DialogTitle>{displayName}</DialogTitle>
      <DialogContent>
        <List>
          {profileMenuItems.map((menuItem) => (
            <UserProfileMenuItem key={menuItem.id} mobile menuItem={menuItem} onMenuItemClick={handleMenuItemClick} />
          ))}
          {!!profileMenuItems.length && <Divider />}
          <ListItem
            button
            disableGutters
            onClick={onLogoutClick}
            className="MobileProfileMenuDialog-profileMenuItem"
            disabled={!oidcConfig}
          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText>{t('menu.logout')}</ListItemText>
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions>
        <Button primary onClick={handleCloseClick}>
          {t('buttons.cancel')}
        </Button>
      </DialogActions>
    </MobileProfileMenuDialogRoot>
  );
};

export default MobileProfileMenuDialog;
